import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Invoice {
  id: string;
  invoice_number: string;
  recipient_email: string;
  total_amount: number;
  currency: string;
  status: string;
  description?: string;
  created_at: string;
}

export default function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    recipient_email: '',
    total_amount: '',
    currency: 'USD',
    description: '',
  });

  useEffect(() => {
    loadInvoices();
  }, []);

  async function loadInvoices() {
    try {
      setLoading(true);
      const res = await api.get('/api/invoices');
      setInvoices(res.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await api.post('/api/invoices', {
        recipient_email: form.recipient_email,
        total_amount: parseFloat(form.total_amount),
        currency: form.currency,
        description: form.description,
      });
      setForm({ recipient_email: '', total_amount: '', currency: 'USD', description: '' });
      loadInvoices();
    } catch (err: any) {
      setError(err.message);
    }
  }

  return (
    <div>
      <h1 style={styles.heading}>Invoices</h1>
      {error && <div style={styles.error}>{error}</div>}

      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          placeholder="Recipient Email"
          value={form.recipient_email}
          onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
          style={styles.input}
          required
        />
        <input
          placeholder="Amount"
          type="number"
          step="0.01"
          value={form.total_amount}
          onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
          style={styles.input}
          required
        />
        <input
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          style={styles.input}
        />
        <button type="submit" style={styles.button}>Create Invoice</button>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Recipient</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td style={styles.td}>{inv.invoice_number}</td>
                <td style={styles.td}>{inv.recipient_email}</td>
                <td style={styles.td}>{inv.description || '-'}</td>
                <td style={styles.td}>{inv.currency} {inv.total_amount.toFixed(2)}</td>
                <td style={styles.td}>{inv.status}</td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af' }}>
                  No invoices yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12 },
  form: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  input: { padding: '8px 10px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 6, minWidth: 140 },
  button: { padding: '8px 16px', fontSize: 14, background: '#111827', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#6b7280' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
};
